import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl, toUploadUrl } from '../config/api';
import { useBooking } from '../hooks/useBookings';
import { addonServiceAPI, couponAPI, packageAPI } from '../services/api';
import walletService from '../services/walletService';
import './../styles/Booking.css';

const Booking = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [packageData, setPackageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const { createBooking, loading: bookingLoading } = useBooking();
    
    const [walletBalance, setWalletBalance] = useState(0);
    const [useWallet, setUseWallet] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        adults: 2,
        children: 0,
        roomsCount: 1,
        travelDate: '',
        specialRequests: '',
        paymentMethod: 'full',
        partialAmount: ''
    });

    const [currentStep, setCurrentStep] = useState(1);
    const [formErrors, setFormErrors] = useState({});
    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponMessage, setCouponMessage] = useState('');
    const [addonServices, setAddonServices] = useState([]);
    const [addonsLoading, setAddonsLoading] = useState(false);
    const [selectedAddons, setSelectedAddons] = useState({});

    const minimumTravelers = useMemo(() => {
        if (!packageData) return 1;

        const category = String(packageData.package_category || '').trim().toLowerCase();
        const configuredMinPax = Number(packageData.min_pax || 0);

        let categoryMinimum = 1;
        if (category === 'corporate') categoryMinimum = 10;
        if (category === 'group') categoryMinimum = 4;

        return Math.max(categoryMinimum, configuredMinPax || 0, 1);
    }, [packageData]);

    const totalTravelers = useMemo(() => {
        return Number(formData.adults || 0) + Number(formData.children || 0);
    }, [formData.adults, formData.children]);

    const minimumAdultsAllowed = useMemo(() => {
        return Math.max(1, minimumTravelers - Number(formData.children || 0));
    }, [formData.children, minimumTravelers]);

    const getImageUrl = useCallback((imagePath) => {
        return toUploadUrl(imagePath, {
            fallback: 'https://placehold.co/300x200/1a1a1a/ffffff?text=No+Image'
        });
    }, []);

    const getTripDays = useCallback((duration) => {
        const matches = String(duration || '').match(/\d+/g) || [];
        const numbers = matches.map(Number).filter((num) => Number.isFinite(num) && num > 0);
        return numbers.length ? Math.max(...numbers) : 1;
    }, []);

    const fetchPackage = useCallback(async () => {
        try {
            setLoading(true);
            const response = await packageAPI.getPackageById(id);
            const data = {
                ...response.data,
                image: getImageUrl(response.data.image),
                gallery: response.data.gallery?.map(img => getImageUrl(img))
            };
            setPackageData(data);
        } catch (err) {
            console.error('Error fetching package:', err);
        } finally {
            setLoading(false);
        }
    }, [getImageUrl, id]);

    const fetchApplicableAddons = useCallback(async () => {
        try {
            setAddonsLoading(true);
            const response = await addonServiceAPI.getApplicableForPackage(id);
            const services = response.data?.data || [];

            setAddonServices(
                services.map((service) => ({
                    ...service,
                    price: Number(service.price || 0),
                    image: getImageUrl(service.image)
                }))
            );
        } catch (error) {
            console.error('Error fetching add-on services:', error);
            setAddonServices([]);
        } finally {
            setAddonsLoading(false);
        }
    }, [getImageUrl, id]);

    const checkAuthAndWallet = useCallback(async () => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
            try {
                const user = JSON.parse(userData);
                setIsLoggedIn(true);
                setFormData(prev => ({
                    ...prev,
                    name: user.name || user.username || '',
                    email: user.email || '',
                    phone: user.phone || ''
                }));
                
                const result = await walletService.getBalance();
                if (result.success) {
                    setWalletBalance(result.balance);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        }
        setAuthChecked(true);
    }, []);

    useEffect(() => {
        fetchPackage();
    }, [fetchPackage]);

    useEffect(() => {
        fetchApplicableAddons();
    }, [fetchApplicableAddons]);

    useEffect(() => {
        checkAuthAndWallet();
    }, [checkAuthAndWallet]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (formErrors[e.target.name]) {
            setFormErrors({
                ...formErrors,
                [e.target.name]: null
            });
        }
    };

    const validateStep1 = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Full name is required';
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }
        if (!formData.phone.trim()) {
            errors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(formData.phone)) {
            errors.phone = 'Phone number must be 10 digits';
        }
        if (!formData.travelDate) errors.travelDate = 'Travel date is required';
        if (totalTravelers < minimumTravelers) {
            errors.travelers = `Minimum ${minimumTravelers} travelers required for this package`;
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        if (validateStep1()) {
            setCurrentStep(2);
            window.scrollTo(0, 0);
        }
    };

    const handlePrevious = () => {
        setCurrentStep(1);
        window.scrollTo(0, 0);
    };

    const subtotal = useMemo(() => {
        if (!packageData) return 0;
        return packageData.price * (parseInt(formData.adults) + parseInt(formData.children) * 0.5);
    }, [packageData, formData.adults, formData.children]);

    const gst = useMemo(() => subtotal * 0.05, [subtotal]);
    const tripDays = useMemo(() => getTripDays(packageData?.duration), [getTripDays, packageData?.duration]);
    const selectedAddonList = useMemo(() => (
        addonServices
            .filter((service) => selectedAddons[service.id])
            .map((service) => ({
                ...service,
                quantity: Math.max(1, Number(selectedAddons[service.id]?.quantity || 1))
            }))
    ), [addonServices, selectedAddons]);
    const addonTotal = useMemo(() => {
        return selectedAddonList.reduce((sum, addon) => {
            let linePrice = addon.price;
            if (addon.price_type === 'per_person') {
                linePrice = addon.price * totalTravelers;
            } else if (addon.price_type === 'per_day') {
                linePrice = addon.price * tripDays;
            } else if (addon.price_type === 'per_room') {
                linePrice = addon.price * Math.max(1, Number(formData.roomsCount || 1));
            }

            return sum + (linePrice * addon.quantity);
        }, 0);
    }, [formData.roomsCount, selectedAddonList, totalTravelers, tripDays]);
    const totalBeforeCoupon = useMemo(() => subtotal + gst + addonTotal, [subtotal, gst, addonTotal]);
    const couponDiscount = useMemo(() => {
        return Math.min(Number(appliedCoupon?.amount || 0), totalBeforeCoupon);
    }, [appliedCoupon, totalBeforeCoupon]);
    const total = useMemo(() => Math.max(totalBeforeCoupon - couponDiscount, 0), [couponDiscount, totalBeforeCoupon]);
    const advanceAmount = useMemo(() => total * 0.2, [total]);
    const isPartialPaymentEnabled = useMemo(() => {
        if (!packageData) return false;
        return Number(packageData.partial_enable) === 1;
    }, [packageData]);
    const normalizedPartialAmount = useMemo(() => {
        const parsedAmount = Number(formData.partialAmount);
        return Number.isFinite(parsedAmount) ? parsedAmount : 0;
    }, [formData.partialAmount]);

    const selectedPaymentAmount = useMemo(() => {
        if (formData.paymentMethod === 'advance') return advanceAmount;
        if (formData.paymentMethod === 'partial' && isPartialPaymentEnabled) return normalizedPartialAmount;
        return total;
    }, [advanceAmount, formData.paymentMethod, isPartialPaymentEnabled, normalizedPartialAmount, total]);

    const walletDeduction = useMemo(() => {
        if (!useWallet) return 0;
        return Math.min(walletBalance, selectedPaymentAmount);
    }, [useWallet, selectedPaymentAmount, walletBalance]);

    const payableAmount = useMemo(() => {
        const remaining = selectedPaymentAmount - walletDeduction;
        return Math.max(0, remaining);
    }, [selectedPaymentAmount, walletDeduction]);

    const remainingAfterSelectedPayment = useMemo(() => {
        return Math.max(0, total - selectedPaymentAmount);
    }, [selectedPaymentAmount, total]);

    const paymentMethodLabel = useMemo(() => {
        if (formData.paymentMethod === 'advance') return 'advance';
        if (formData.paymentMethod === 'partial' && isPartialPaymentEnabled) return 'partial';
        return 'full';
    }, [formData.paymentMethod, isPartialPaymentEnabled]);

    useEffect(() => {
        if (!appliedCoupon) return;

        if (totalBeforeCoupon < Number(appliedCoupon.min_booking_amount || 0)) {
            setAppliedCoupon(null);
            setCouponMessage('Coupon removed because booking amount no longer meets minimum value.');
        }
    }, [appliedCoupon, totalBeforeCoupon]);

    const handleApplyCoupon = async () => {
        const normalizedCode = couponCode.trim().toUpperCase();
        if (!normalizedCode) {
            setCouponMessage('Please enter a coupon code.');
            return;
        }

        try {
            setCouponLoading(true);
            setCouponMessage('');
            const response = await couponAPI.validateCoupon(normalizedCode, totalBeforeCoupon);
            const coupon = response.data?.data || null;
            setAppliedCoupon(coupon);
            setCouponCode(coupon?.code || normalizedCode);
            setCouponMessage(`Coupon ${coupon?.code} applied. You saved ₹${Number(coupon?.amount || 0).toLocaleString('en-IN')}.`);
        } catch (error) {
            setAppliedCoupon(null);
            setCouponMessage(error.response?.data?.message || 'Unable to apply coupon.');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponMessage('Coupon removed.');
    };

    useEffect(() => {
        if (!isPartialPaymentEnabled && formData.paymentMethod === 'partial') {
            setFormData((prev) => ({
                ...prev,
                paymentMethod: 'full',
                partialAmount: ''
            }));
        }
    }, [formData.paymentMethod, isPartialPaymentEnabled]);

    useEffect(() => {
        if (!packageData) return;

        setFormData((prev) => {
            const currentAdults = Number(prev.adults || 0);
            if (currentAdults >= minimumAdultsAllowed) {
                return prev;
            }

            return {
                ...prev,
                adults: minimumAdultsAllowed
            };
        });
    }, [minimumAdultsAllowed, packageData]);

    useEffect(() => {
        setFormData((prev) => {
            const derivedRooms = Math.max(1, Number(prev.roomsCount || Math.ceil(Number(prev.adults || 1) / 2)));
            if (Number(prev.roomsCount || 0) >= 1) {
                return prev;
            }

            return {
                ...prev,
                roomsCount: derivedRooms
            };
        });
    }, [formData.adults]);

    const toggleAddon = (addonId) => {
        setSelectedAddons((prev) => {
            if (prev[addonId]) {
                const next = { ...prev };
                delete next[addonId];
                return next;
            }

            return {
                ...prev,
                [addonId]: {
                    addon_service_id: addonId,
                    quantity: 1
                }
            };
        });
    };

    const updateAddonQuantity = (addonId, delta) => {
        setSelectedAddons((prev) => {
            const existing = prev[addonId];
            if (!existing) return prev;

            return {
                ...prev,
                [addonId]: {
                    ...existing,
                    quantity: Math.max(1, Number(existing.quantity || 1) + delta)
                }
            };
        });
    };

    // ✅ FIXED: Process payment with proper order creation
    const processPayment = async (bookingSnapshot) => {
        try {
            setProcessingPayment(true);
            
            const amountToPayForGateway = payableAmount;
            
            console.log('💰 Payment Debug:', {
                bookingId: bookingSnapshot.booking_id,
                bookingTotalAmount: bookingSnapshot.advance_amount,
                paymentType: bookingSnapshot.payment_method,
                useWallet,
                walletBalance,
                walletDeduction,
                payableAmount,
                amountToPayForGateway
            });

            // ✅ STEP 1: Apply wallet payment first (if any)
            if (useWallet && walletDeduction > 0) {
                console.log(`💰 Applying wallet payment: ₹${walletDeduction}`);
                const walletResult = await walletService.applyPayment(walletDeduction, bookingSnapshot.booking_id, 'package');
                if (!walletResult.success) {
                    throw new Error(walletResult.message || 'Wallet payment failed');
                }
                console.log('✅ Wallet payment applied successfully');
            }

            // ✅ STEP 2: If remaining amount > 0, process Razorpay
            if (payableAmount > 0) {
                console.log(`💳 Creating Razorpay order for: ₹${payableAmount}`);
                
                // ✅ Create order for EXACT remaining amount
                const orderResponse = await axios.post(buildApiUrl('/payments/create-order'), {
                    amount: payableAmount, // ← This is the remaining amount after wallet
                    currency: 'INR',
                    receipt: `booking_${bookingSnapshot.booking_id}_${Date.now()}`,
                    notes: {
                        booking_id: bookingSnapshot.booking_id,
                        package_id: id,
                        wallet_used: walletDeduction,
                        total_amount: bookingSnapshot.total_amount,
                        payment_type: bookingSnapshot.payment_method
                    }
                });

                if (!orderResponse.data.success) {
                    throw new Error(orderResponse.data.message || 'Failed to create payment order');
                }

                const order = orderResponse.data;
                console.log('✅ Order created:', order);
                
                // ✅ Initialize Razorpay
                const razorpayOptions = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    amount: order.amount,
                    currency: order.currency,
                    name: 'RedGoTrip',
                    description: `${bookingSnapshot.payment_method.charAt(0).toUpperCase() + bookingSnapshot.payment_method.slice(1)} Payment for ${packageData.title}`,
                    order_id: order.orderId,
                    prefill: {
                        name: formData.name,
                        email: formData.email,
                        contact: formData.phone
                    },
                    notes: {
                        booking_id: bookingSnapshot.booking_id,
                        package_id: id,
                        wallet_used: walletDeduction
                    },
                    theme: {
                        color: '#ec3134'
                    },
                    handler: async (response) => {
                        try {
                            console.log('✅ Razorpay success response:', response);
                            
                            // ✅ Verify payment
                            const verifyResponse = await axios.post(buildApiUrl('/payments/verify'), {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                booking_id: bookingSnapshot.booking_id
                            });

                            if (!verifyResponse.data.success) {
                                throw new Error('Payment verification failed');
                            }

                            console.log('✅ Payment verified successfully');
                            navigate(`/booking-success/${bookingSnapshot.booking_id}?payment=success`, {
                                state: {
                                    booking: {
                                        ...bookingSnapshot,
                                        package_title: packageData.title,
                                        payment_status: 'completed',
                                        booking_status: 'confirmed',
                                        transaction_id: response.razorpay_payment_id,
                                        created_at: new Date().toISOString(),
                                        destinations: packageData.destinations,
                                        duration: packageData.duration,
                                        package_id: parseInt(id),
                                        addon_services: bookingSnapshot.selected_addons || []
                                    }
                                }
                            });
                        } catch (error) {
                            console.error('Payment verification error:', error);
                            alert(error.message || 'Payment verification failed. Please contact support.');
                        } finally {
                            setProcessingPayment(false);
                        }
                    },
                    modal: {
                        ondismiss: () => {
                            console.log('❌ Razorpay modal closed');
                            setProcessingPayment(false);
                            alert('Payment was cancelled. You can retry from My Bookings.');
                        }
                    }
                };
                
                const razorpay = new window.Razorpay(razorpayOptions);
                razorpay.open();
                
            } else {
                // ✅ Full payment via wallet, no Razorpay needed
                console.log('✅ Full payment made via wallet, no Razorpay needed');
                navigate(`/booking-success/${bookingSnapshot.booking_id}?payment=success`, {
                    state: {
                        booking: {
                            ...bookingSnapshot,
                            package_title: packageData.title,
                            payment_status: 'completed',
                            booking_status: 'confirmed',
                            transaction_id: '',
                            created_at: new Date().toISOString(),
                            destinations: packageData.destinations,
                            duration: packageData.duration,
                            package_id: parseInt(id),
                            addon_services: bookingSnapshot.selected_addons || []
                        }
                    }
                });
                setProcessingPayment(false);
            }
            
        } catch (error) {
            console.error('Payment error:', error);
            alert(error.message || 'Payment failed. Please try again.');
            setProcessingPayment(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (currentStep === 1) {
            handleNext();
            return;
        }
        
        try {
            const totalAmount = selectedPaymentAmount;

            if (totalTravelers < minimumTravelers) {
                alert(`This package requires at least ${minimumTravelers} travelers to book.`);
                setCurrentStep(1);
                return;
            }

            if (formData.paymentMethod === 'partial') {
                if (!normalizedPartialAmount || Number.isNaN(normalizedPartialAmount)) {
                    alert('Please enter a partial payment amount.');
                    return;
                }

                if (normalizedPartialAmount < 100) {
                    alert('Partial payment must be at least ₹100.');
                    return;
                }

                if (normalizedPartialAmount >= total) {
                    alert('Partial amount should be less than total amount. Choose Full Payment instead.');
                    return;
                }
            }
            
            // ✅ Validation for wallet
            if (useWallet && walletBalance <= 0) {
                alert('Your wallet balance is ₹0. Please disable wallet or select another payment method.');
                return;
            }

            // ✅ Case 1: Wallet has sufficient balance for full amount
            if (useWallet && walletBalance >= totalAmount) {
                const confirmMsg = `Your wallet balance (₹${walletBalance.toLocaleString()}) is sufficient to cover the full amount ₹${totalAmount.toLocaleString()}. 
                Do you want to pay directly from wallet?`;
                
                if (!window.confirm(confirmMsg)) {
                    return;
                }
                // Proceed - will use only wallet
            }
            
            // ✅ Case 2: Wallet has partial balance
            else if (useWallet && walletBalance > 0 && walletBalance < totalAmount) {
                const confirmMsg = `Your wallet balance is ₹${walletBalance.toLocaleString()}. 
                ₹${walletDeduction.toLocaleString()} will be deducted from wallet.
                Remaining ₹${payableAmount.toLocaleString()} will be paid via card/UPI.
                Do you want to continue?`;
                
                if (!window.confirm(confirmMsg)) {
                    return;
                }
            }
            
            // ✅ Case 3: No wallet or wallet disabled
            else if (!useWallet) {
                console.log('Processing without wallet');
            }

            console.log('📝 Creating booking with data:', {
                package_id: parseInt(id),
                total_amount: total,
                payable_amount: payableAmount,
                wallet_deduction: walletDeduction,
                use_wallet: useWallet
            });

            // ✅ Create booking
            const bookingData = {
                package_id: parseInt(id),
                customer_name: formData.name,
                customer_email: formData.email,
                customer_phone: formData.phone,
                adults: parseInt(formData.adults),
                children: parseInt(formData.children),
                rooms_count: parseInt(formData.roomsCount),
                travel_date: formData.travelDate,
                special_requests: formData.specialRequests,
                total_amount: total,
                original_amount: totalBeforeCoupon,
                advance_amount: totalAmount,
                payment_method: paymentMethodLabel,
                wallet_used: useWallet ? walletDeduction : 0,
                payment_status: payableAmount === 0 ? 'completed' : 'pending',
                coupon_id: appliedCoupon?.id || null,
                coupon_code: appliedCoupon?.code || null,
                coupon_discount: couponDiscount,
                selected_addons: selectedAddonList.map((addon) => ({
                    addon_service_id: addon.id,
                    quantity: addon.quantity
                }))
            };

            const response = await createBooking(bookingData);
            const bookingId = response.bookingId;
            const bookingSnapshot = response.data;
            
            if (!bookingId) {
                throw new Error('Booking ID not received');
            }
            
            console.log('✅ Booking created with ID:', bookingId);
            
            // ✅ Process payment
            await processPayment(bookingSnapshot || {
                booking_id: bookingId,
                total_amount: total,
                original_amount: totalBeforeCoupon,
                advance_amount: totalAmount,
                payment_method: paymentMethodLabel,
                selected_addons: selectedAddonList
            });
            
        } catch (err) {
            console.error('Booking failed:', err);
            alert(err.message || 'Failed to create booking. Please try again.');
        }
    };

    if (loading || !authChecked) {
        return (
            <div className="booking-loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading booking details...</p>
                </div>
            </div>
        );
    }

    if (!packageData) {
        return (
            <div className="booking-error">
                <div className="error-content">
                    <i className="fas fa-exclamation-circle"></i>
                    <h3>Package Not Found</h3>
                    <p>The package you're looking for doesn't exist or has been removed.</p>
                    <Link to="/packages" className="error-btn">
                        <i className="fas fa-arrow-left"></i>
                        Browse Packages
                    </Link>
                </div>
            </div>
        );
    }

    const totalAmount = selectedPaymentAmount;

    return (
        <div className="booking-page">
            <div className="booking-hero">
                <div className="hero-content">
                    <span className="booking-hero-badge">Secure Booking Checkout</span>
                    <h1>Complete Your Booking</h1>
                    <p>Review your trip details, choose payment preference and confirm your journey with confidence.</p>
                    <div className="hero-highlights">
                        <div className="hero-highlight">
                            <i className="fas fa-map-location-dot"></i>
                            <span>{packageData.title}</span>
                        </div>
                        <div className="hero-highlight">
                            <i className="fas fa-users"></i>
                            <span>{formData.adults + formData.children} Traveller{formData.adults + formData.children > 1 ? 's' : ''}</span>
                        </div>
                        <div className="hero-highlight">
                            <i className="fas fa-shield-heart"></i>
                            <span>Instant Support</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="booking-container">
                <div className="progress-section">
                    <div className="progress-steps">
                        <div className={`step-item ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>
                            <div className="step-number">{currentStep > 1 ? <i className="fas fa-check"></i> : '1'}</div>
                            <div className="step-content">
                                <span className="step-label">STEP 1</span>
                                <span className="step-title">Traveler Details</span>
                            </div>
                        </div>
                        <div className={`step-line ${currentStep > 1 ? 'active' : ''}`}></div>
                        <div className={`step-item ${currentStep === 2 ? 'active' : ''}`}>
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <span className="step-label">STEP 2</span>
                                <span className="step-title">Payment & Review</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="booking-grid">
                    <div className="form-column">
                        <div className="form-card">
                            <form onSubmit={handleSubmit}>
                                {currentStep === 1 && (
                                    <div className="step-content fade-in">
                                        <div className="section-intro">
                                            <span>Traveler Information</span>
                                            <p>Fill in the main contact and travel preferences for this booking.</p>
                                        </div>
                                        <h2 className="form-title">
                                            <i className="fas fa-user"></i>
                                            Personal Information
                                        </h2>
                                        
                                        <div className="form-group">
                                            <label>Full Name <span className="required">*</span></label>
                                            <div className="input-wrapper">
                                                <i className="fas fa-user input-icon"></i>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className={`form-input ${formErrors.name ? 'error' : ''}`}
                                                    placeholder="Enter your full name"
                                                />
                                            </div>
                                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Email Address <span className="required">*</span></label>
                                                <div className="input-wrapper">
                                                    <i className="fas fa-envelope input-icon"></i>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`form-input ${formErrors.email ? 'error' : ''}`}
                                                        placeholder="your@email.com"
                                                    />
                                                </div>
                                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                                            </div>

                                            <div className="form-group">
                                                <label>Phone Number <span className="required">*</span></label>
                                                <div className="input-wrapper">
                                                    <i className="fas fa-phone input-icon"></i>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        className={`form-input ${formErrors.phone ? 'error' : ''}`}
                                                        placeholder="10 digit mobile number"
                                                    />
                                                </div>
                                                {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                                            </div>
                                        </div>

                                        <h2 className="form-title" style={{ marginTop: '2rem' }}>
                                            <i className="fas fa-plane"></i>
                                            Travel Details
                                        </h2>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Adults (12+ years) <span className="required">*</span></label>
                                                <div className="counter-input">
                                                    <button type="button" onClick={() => setFormData({...formData, adults: Math.max(minimumAdultsAllowed, formData.adults - 1)})}>
                                                        <i className="fas fa-minus"></i>
                                                    </button>
                                                    <input type="number" name="adults" min={minimumAdultsAllowed} value={formData.adults} onChange={handleChange} readOnly />
                                                    <button type="button" onClick={() => setFormData({...formData, adults: formData.adults + 1})}>
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Children (5-12 years)</label>
                                                <div className="counter-input">
                                                    <button type="button" onClick={() => setFormData({...formData, children: Math.max(0, formData.children - 1)})}>
                                                        <i className="fas fa-minus"></i>
                                                    </button>
                                                    <input type="number" name="children" min="0" value={formData.children} onChange={handleChange} readOnly />
                                                    <button type="button" onClick={() => setFormData({...formData, children: formData.children + 1})}>
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Rooms Required</label>
                                            <div className="counter-input">
                                                <button type="button" onClick={() => setFormData({...formData, roomsCount: Math.max(1, Number(formData.roomsCount) - 1)})}>
                                                    <i className="fas fa-minus"></i>
                                                </button>
                                                <input type="number" name="roomsCount" min="1" value={formData.roomsCount} onChange={handleChange} readOnly />
                                                <button type="button" onClick={() => setFormData({...formData, roomsCount: Number(formData.roomsCount) + 1})}>
                                                    <i className="fas fa-plus"></i>
                                                </button>
                                            </div>
                                        </div>

                                        {(String(packageData?.package_category || '').toLowerCase() === 'corporate' || String(packageData?.package_category || '').toLowerCase() === 'group') && (
                                            <div className="form-group">
                                                <span className="error-message" style={{ display: 'block' }}>
                                                    {String(packageData?.package_category || '').toLowerCase() === 'corporate'
                                                        ? 'Corporate packages require a minimum of 10 travelers.'
                                                        : 'Group packages require a minimum of 4 travelers.'}
                                                </span>
                                                {formErrors.travelers && <span className="error-message">{formErrors.travelers}</span>}
                                            </div>
                                        )}

                                        <div className="form-group">
                                            <label>Preferred Travel Date <span className="required">*</span></label>
                                            <div className="input-wrapper">
                                                <i className="fas fa-calendar-alt input-icon"></i>
                                                <input
                                                    type="date"
                                                    name="travelDate"
                                                    value={formData.travelDate}
                                                    onChange={handleChange}
                                                    className={`form-input ${formErrors.travelDate ? 'error' : ''}`}
                                                    min={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            {formErrors.travelDate && <span className="error-message">{formErrors.travelDate}</span>}
                                        </div>

                                        <div className="form-group">
                                            <label>Special Requests (Optional)</label>
                                            <textarea
                                                name="specialRequests"
                                                className="form-textarea"
                                                rows="4"
                                                value={formData.specialRequests}
                                                onChange={handleChange}
                                                placeholder="Any specific requirements, dietary restrictions, or preferences..."
                                            ></textarea>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 2 && (
                                    <div className="step-content fade-in">
                                        <div className="section-intro">
                                            <span>Payment Review</span>
                                            <p>Choose how you want to pay and confirm the final amount before checkout.</p>
                                        </div>
                                        <h2 className="form-title">
                                            <i className="fas fa-credit-card"></i>
                                            Payment Details
                                        </h2>
                                        
                                        <div className="booking-summary-card">
                                            <h3>Booking Summary</h3>
                                            <div className="summary-items">
                                                <div className="summary-item">
                                                    <span>Package:</span>
                                                    <span>{packageData.title}</span>
                                                </div>
                                                <div className="summary-item">
                                                    <span>Travel Date:</span>
                                                    <span>{new Date(formData.travelDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                                </div>
                                                <div className="summary-item">
                                                    <span>Travelers:</span>
                                                    <span>{formData.adults} Adult{formData.adults > 1 ? 's' : ''}{formData.children > 0 ? `, ${formData.children} Child${formData.children > 1 ? 'ren' : ''}` : ''}</span>
                                                </div>
                                                <div className="summary-item">
                                                    <span>Rooms:</span>
                                                    <span>{formData.roomsCount}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="addons-card">
                                            <div className="coupon-card-header">
                                                <div>
                                                    <h3>
                                                        <i className="fas fa-concierge-bell"></i>
                                                        Enhance Your Trip with Add-on Services
                                                    </h3>
                                                    <p>Select optional extras to personalize this package.</p>
                                                </div>
                                            </div>
                                            {addonsLoading ? (
                                                <div className="addons-empty-state">Loading add-on services...</div>
                                            ) : addonServices.length === 0 ? (
                                                <div className="addons-empty-state">No add-on services available for this package right now.</div>
                                            ) : (
                                                <div className="addons-grid">
                                                    {addonServices.map((addon) => {
                                                        const isSelected = Boolean(selectedAddons[addon.id]);
                                                        const quantity = selectedAddons[addon.id]?.quantity || 1;

                                                        return (
                                                            <button
                                                                key={addon.id}
                                                                type="button"
                                                                className={`addon-card ${isSelected ? 'selected' : ''}`}
                                                                onClick={() => toggleAddon(addon.id)}
                                                            >
                                                                <img src={addon.image} alt={addon.title} className="addon-card-image" />
                                                                <div className="addon-card-content">
                                                                    <div className="addon-card-top">
                                                                        <h4>{addon.title}</h4>
                                                                        <span className="addon-price-type">{addon.price_type.replace('_', ' ')}</span>
                                                                    </div>
                                                                    <p>{addon.short_description || 'Optional premium service for your trip.'}</p>
                                                                    <div className="addon-card-footer">
                                                                        <strong>₹{addon.price.toLocaleString('en-IN')}</strong>
                                                                        <span>{addon.price_type === 'fixed' ? 'fixed' : addon.price_type.replace('_', ' ')}</span>
                                                                    </div>
                                                                    {isSelected && (
                                                                        <div className="addon-quantity-row" onClick={(e) => e.stopPropagation()}>
                                                                            <button type="button" onClick={() => updateAddonQuantity(addon.id, -1)}>
                                                                                <i className="fas fa-minus"></i>
                                                                            </button>
                                                                            <span>{quantity}</span>
                                                                            <button type="button" onClick={() => updateAddonQuantity(addon.id, 1)}>
                                                                                <i className="fas fa-plus"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* ✅ Wallet Option - Fixed */}
                                        {isLoggedIn && walletBalance > 0 && (
                                            <div className="wallet-option-card">
                                                <div className="wallet-option-header">
                                                    <i className="fas fa-wallet"></i>
                                                    <div>
                                                        <strong>Wallet Balance Available</strong>
                                                        <span>₹{walletBalance.toLocaleString()}</span>
                                                    </div>
                                                </div>
                                                
                                                <label className="wallet-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={useWallet}
                                                        onChange={(e) => setUseWallet(e.target.checked)}
                                                    />
                                                    <span>Use wallet balance for this booking</span>
                                                </label>
                                                
                                                {useWallet && (
                                                    <div className="wallet-deduction-info">
                                                        <div className="deduction-row">
                                                            <span>Selected Payment:</span>
                                                            <span>₹{totalAmount.toLocaleString()}</span>
                                                        </div>
                                                        <div className="deduction-row">
                                                            <span>Wallet Balance:</span>
                                                            <span className="wallet-amount">₹{walletBalance.toLocaleString()}</span>
                                                        </div>
                                                        <div className="deduction-row">
                                                            <span>Wallet Deduction:</span>
                                                            <span className="wallet-deduction-amount">-₹{walletDeduction.toLocaleString()}</span>
                                                        </div>
                                                        <div className="deduction-row total">
                                                            <span>Amount to Pay:</span>
                                                            <span className="payable-amount">₹{payableAmount.toLocaleString()}</span>
                                                        </div>
                                                        {payableAmount === 0 && (
                                                            <div className="success-message">
                                                                <i className="fas fa-check-circle"></i>
                                                                Full payment covered by wallet!
                                                            </div>
                                                        )}
                                                        {walletBalance < totalAmount && (
                                                            <div className="info-message">
                                                                <i className="fas fa-info-circle"></i>
                                                                Remaining amount will be paid via Razorpay
                                                            </div>
                                                        )}
                                                        {remainingAfterSelectedPayment > 0 && (
                                                            <div className="info-message">
                                                                <i className="fas fa-calendar-check"></i>
                                                                Balance due later: ₹{remainingAfterSelectedPayment.toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="coupon-card">
                                            <div className="coupon-card-header">
                                                <div>
                                                    <h3>
                                                        <i className="fas fa-ticket-alt"></i>
                                                        Apply Coupon
                                                    </h3>
                                                    <p>Add a coupon code to reduce your total payable amount.</p>
                                                </div>
                                                {appliedCoupon && <span className="coupon-badge-applied">{appliedCoupon.code}</span>}
                                            </div>
                                            <div className="coupon-row">
                                                <input
                                                    type="text"
                                                    className="coupon-input"
                                                    placeholder="Enter coupon code"
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                    disabled={couponLoading}
                                                />
                                                {appliedCoupon ? (
                                                    <button type="button" className="remove-coupon-btn" onClick={handleRemoveCoupon}>
                                                        Remove
                                                    </button>
                                                ) : (
                                                    <button type="button" className="apply-coupon-btn" onClick={handleApplyCoupon} disabled={couponLoading}>
                                                        {couponLoading ? 'Applying...' : 'Apply'}
                                                    </button>
                                                )}
                                            </div>
                                            {couponMessage && (
                                                <div className={`coupon-message ${appliedCoupon ? 'success' : 'error'}`}>
                                                    {couponMessage}
                                                </div>
                                            )}
                                        </div>

                                        <div className="payment-options">
                                            <h3>Select Payment Method</h3>
                                            
                                            <label className={`payment-option ${formData.paymentMethod === 'full' ? 'selected' : ''}`}>
                                                <input type="radio" name="paymentMethod" value="full" checked={formData.paymentMethod === 'full'} onChange={handleChange} />
                                                <div className="option-content">
                                                    <div className="option-header">
                                                        <i className="fas fa-credit-card"></i>
                                                        <div>
                                                            <strong>Pay in Full</strong>
                                                            <span className="option-desc">Pay entire amount now</span>
                                                        </div>
                                                    </div>
                                                    <div className="option-price">
                                                        <span className="price">₹{total.toFixed(2)}</span>
                                                        <span className="badge">Save 5%</span>
                                                    </div>
                                                </div>
                                            </label>

                                            <label className={`payment-option ${formData.paymentMethod === 'advance' ? 'selected' : ''}`}>
                                                <input type="radio" name="paymentMethod" value="advance" checked={formData.paymentMethod === 'advance'} onChange={handleChange} />
                                                <div className="option-content">
                                                    <div className="option-header">
                                                        <i className="fas fa-clock"></i>
                                                        <div>
                                                            <strong>Pay Advance (20%)</strong>
                                                            <span className="option-desc">Pay now, remaining later</span>
                                                        </div>
                                                    </div>
                                                    <div className="option-price">
                                                        <span className="price">₹{advanceAmount.toFixed(2)}</span>
                                                        <span className="remaining">Remaining: ₹{(total - advanceAmount).toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </label>

                                            {isPartialPaymentEnabled && (
                                                <label className={`payment-option ${formData.paymentMethod === 'partial' ? 'selected' : ''}`}>
                                                    <input type="radio" name="paymentMethod" value="partial" checked={formData.paymentMethod === 'partial'} onChange={handleChange} />
                                                    <div className="option-content option-content-stacked">
                                                        <div className="option-header">
                                                            <i className="fas fa-sliders"></i>
                                                            <div>
                                                                <strong>Pay Custom Partial</strong>
                                                                <span className="option-desc">Choose any amount now, minimum ₹100</span>
                                                            </div>
                                                        </div>
                                                        <div className="partial-payment-box">
                                                            <label htmlFor="partialAmount">Partial Amount</label>
                                                            <input
                                                                id="partialAmount"
                                                                type="number"
                                                                name="partialAmount"
                                                                min="100"
                                                                max={Math.max(100, Math.floor(total - 1))}
                                                                step="1"
                                                                value={formData.partialAmount}
                                                                onChange={handleChange}
                                                                className="partial-payment-input"
                                                                placeholder="Enter amount to pay now"
                                                                disabled={formData.paymentMethod !== 'partial'}
                                                            />
                                                            <span className="remaining">
                                                                {normalizedPartialAmount > 0
                                                                    ? `Remaining later: ₹${remainingAfterSelectedPayment.toFixed(2)}`
                                                                    : `Minimum payable now: ₹100`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            )}
                                        </div>

                                        <div className="secure-payment">
                                            <div className="secure-header">
                                                <i className="fas fa-shield-alt"></i>
                                                <h4>100% Secure Payment</h4>
                                            </div>
                                            <p>Your payment information is encrypted and secure. We accept all major payment methods.</p>
                                            <div className="payment-methods">
                                                <i className="fab fa-cc-visa"></i>
                                                <i className="fab fa-cc-mastercard"></i>
                                                <i className="fab fa-cc-amex"></i>
                                                <i className="fab fa-cc-paypal"></i>
                                                <i className="fas fa-credit-card"></i>
                                            </div>
                                        </div>

                                        <div className="terms-agreement">
                                            <input type="checkbox" id="terms" required />
                                            <label htmlFor="terms">
                                                I agree to the <Link to="/terms">terms and conditions</Link> and <Link to="/privacy">privacy policy</Link>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="form-actions">
                                    {currentStep === 2 && (
                                        <button type="button" className="btn-back" onClick={handlePrevious}>
                                            <i className="fas fa-arrow-left"></i> Back
                                        </button>
                                    )}
                                    <button 
                                        type="submit" 
                                        className="btn-continue"
                                        disabled={bookingLoading || processingPayment}
                                    >
                                        {processingPayment ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                                        ) : bookingLoading ? (
                                            'Please wait...'
                                        ) : (
                                            currentStep === 1 ? 'Continue to Payment' : `Pay ₹${payableAmount.toLocaleString()}`
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="summary-column">
                        <div className="summary-card sticky">
                            <div className="summary-topbar">
                                <span className="summary-chip">Trip Snapshot</span>
                                <span className="summary-status">{currentStep === 1 ? 'Draft Booking' : 'Ready to Pay'}</span>
                            </div>
                            <h3>Your Booking Summary</h3>
                            
                            <div className="package-preview">
                                <img src={packageData.image} alt={packageData.title} className="package-image" />
                                <div className="package-info">
                                    <h4>{packageData.title}</h4>
                                    <p className="package-meta"><i className="fas fa-clock"></i> {packageData.duration}</p>
                                    <p className="package-meta"><i className="fas fa-map-marker-alt"></i> {packageData.destinations}</p>
                                </div>
                            </div>

                            <div className="price-breakdown">
                                <h4>Price Breakdown</h4>
                                <div className="breakdown-item">
                                    <span>Package Price (per adult)</span>
                                    <span>₹{packageData.price.toLocaleString()}</span>
                                </div>
                                <div className="breakdown-item">
                                    <span>Adults ({formData.adults})</span>
                                    <span>₹{(packageData.price * formData.adults).toLocaleString()}</span>
                                </div>
                                {formData.children > 0 && (
                                    <div className="breakdown-item">
                                        <span>Children ({formData.children})</span>
                                        <span>₹{(packageData.price * 0.5 * formData.children).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="breakdown-item subtotal">
                                    <span>Subtotal</span>
                                    <span>₹{subtotal.toLocaleString()}</span>
                                </div>
                                <div className="breakdown-item">
                                    <span>GST (5%)</span>
                                    <span>₹{gst.toFixed(2)}</span>
                                </div>
                                {selectedAddonList.map((addon) => {
                                    let lineTotal = addon.price;
                                    if (addon.price_type === 'per_person') {
                                        lineTotal = addon.price * totalTravelers;
                                    } else if (addon.price_type === 'per_day') {
                                        lineTotal = addon.price * tripDays;
                                    } else if (addon.price_type === 'per_room') {
                                        lineTotal = addon.price * Math.max(1, Number(formData.roomsCount || 1));
                                    }
                                    lineTotal *= addon.quantity;

                                    return (
                                        <div className="breakdown-item addon-breakdown" key={addon.id}>
                                            <span>{addon.title} x{addon.quantity}</span>
                                            <span>₹{lineTotal.toLocaleString('en-IN')}</span>
                                        </div>
                                    );
                                })}
                                {selectedAddonList.length > 0 && (
                                    <div className="breakdown-item subtotal">
                                        <span>Add-on Services Total</span>
                                        <span>₹{addonTotal.toLocaleString('en-IN')}</span>
                                    </div>
                                )}
                                {appliedCoupon && (
                                    <div className="breakdown-item discount-row">
                                        <span>Coupon ({appliedCoupon.code})</span>
                                        <span>-₹{couponDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="breakdown-item total">
                                    <span>Total Amount</span>
                                    <span className="total-price">₹{total.toFixed(2)}</span>
                                </div>
                                <div className="breakdown-item total">
                                    <span>Paying Now</span>
                                    <span className="total-price">₹{selectedPaymentAmount.toFixed(2)}</span>
                                </div>
                                {remainingAfterSelectedPayment > 0 && (
                                    <div className="breakdown-item">
                                        <span>Pay Later</span>
                                        <span>₹{remainingAfterSelectedPayment.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            {useWallet && walletDeduction > 0 && (
                                <div className="wallet-summary">
                                    <div className="wallet-row">
                                        <span>Wallet Balance:</span>
                                        <span className="wallet-balance">₹{walletBalance.toLocaleString()}</span>
                                    </div>
                                    <div className="wallet-row">
                                        <span>Wallet Used:</span>
                                        <span className="wallet-deduction">-₹{walletDeduction.toLocaleString()}</span>
                                    </div>
                                    <div className="wallet-row final">
                                        <span>Final Payment:</span>
                                        <span className="final-amount">₹{payableAmount.toLocaleString()}</span>
                                    </div>
                                </div>
                            )}

                            <div className="guarantee-badge">
                                <i className="fas fa-shield-alt"></i>
                                <div>
                                    <strong>Best Price Guarantee</strong>
                                    <p>Found a lower price? We'll match it.</p>
                                </div>
                            </div>

                            <div className="support-info">
                                <p>Need help? Call us</p>
                                <a href="tel:+919335444047" className="support-phone">
                                    <i className="fas fa-phone-alt"></i> +91 9335444047
                                </a>
                            </div>

                            <div className="summary-assurance">
                                <div className="assurance-row">
                                    <i className="fas fa-bolt"></i>
                                    <span>Instant booking support</span>
                                </div>
                                <div className="assurance-row">
                                    <i className="fas fa-lock"></i>
                                    <span>Encrypted checkout</span>
                                </div>
                                <div className="assurance-row">
                                    <i className="fas fa-wallet"></i>
                                    <span>Wallet and Razorpay supported</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Booking;
